import { useState, useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import * as interfaces from 'interfaces';
import { useClientContext, useTranslationContext, useUserContext } from 'hooks';
import { AnswerServiceInstance, TrackerServiceInstance } from 'services';

import CounterButton from 'shared/CounterButton';
import IconAvatar from 'shared/IconAvatar';
import Icons from 'shared/Icons';
import VideoControl from './VideoControl';

import classes from './SelectedAnswer.module.scss';
import ButtonClasses from 'shared/Button/Button.module.scss';

export interface VideoStateInterface {
  started: boolean;
  paused: boolean;
  seeking: boolean;
  seeked: boolean;
  ended: boolean;

  isFullScreen: boolean;
  isFeedbackShown: boolean;
  isFeedbackSubmitted: boolean;
  isDonationBannerShown: boolean;
  isMetaInfoShown: boolean;
}

export interface VideoSubtitleStateInterface {
  index: number;
  currentLine: string | null;
  list: any[] | string[];
  started: boolean;
  enabled: boolean;
}

export default function SelectedAnswer(props: {
  isMobile?: boolean;
  videoRef: any;
  selectedAnswer?: interfaces.AnswerInterface;
  handleVideoUpdate?: (a: interfaces.AnswerInterface) => void;
}) {
  const { isMobile, videoRef } = props;
  const { selectedAnswer, handleVideoUpdate = (a: interfaces.AnswerInterface) => { } } = props;
  const { client } = useClientContext();
  const { user } = useUserContext();
  const { t, translateCustomText, language } = useTranslationContext();

  const [videoState, setVideoState] = useState({} as VideoStateInterface);
  const [subtitles, setVideoSubtitles] = useState({} as VideoSubtitleStateInterface);
  const [muted, setMuted] = useState<boolean>(false);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoBackgroundContext = canvasRef.current?.getContext("2d");

  const drawBackground = () => {
    if (videoRef.current?.paused || videoRef.current?.ended) return;
    videoBackgroundContext?.drawImage(videoRef.current, 0, 0);
    setTimeout(drawBackground, 20);
  }

  const getQuestionTranslation = () => {
    const question = selectedAnswer?.question;

    if (question) {
      if (language !== question.originalLanguage && question.translations)
        return question.translations[language] || question.text;
      return question.text;
    }
  }

  const videoIsPlaying: boolean = (videoState.seeking || (videoState.started && !videoState.paused && !videoState.ended))

  const videoIsPaused: boolean = (
    (!videoState.started || videoState.paused) &&
    !videoState.ended
  )

  const showMetaData: boolean = (videoIsPaused &&
    selectedAnswer?.question?.text !== undefined
  )

  function setSubtitles() {
    let subtitlesText = '';

    if (language !== 'en' && selectedAnswer?.subtitlesTranslations && selectedAnswer?.subtitlesTranslations[language])
      subtitlesText = selectedAnswer?.subtitlesTranslations[language];
    else
      subtitlesText = selectedAnswer?.subtitles || '';

    let lineWidth: number = 450, // pixels
      letterWidth: number = 8,
      letterCount: number = Math.floor(lineWidth / letterWidth),
      words: string[] = subtitlesText.split(' '),
      currentLineSize: number = 0,
      currentLine: string = '',
      lines: any[] = []

    if (!subtitlesText || words.length === 1) return

    words.forEach((word: string, i: number) => {
      const wordSize = word.length

      if (currentLineSize + wordSize + 1 > letterCount) {
        lines[lines.length] = currentLine.replace(/^ /, '').replace(/\s+/g, ' ').replace(/\\n/g, ' ').replace(/\\"/g, '').replace(/\\/g, ' ')
        currentLineSize = 0
        currentLine = ''
      }

      currentLineSize += wordSize + 1

      currentLine = `${currentLine} ${word}`
    })

    setVideoSubtitles(c => ({
      ...c,
      list: lines,
      enabled: subtitlesText !== ''
    }));
  }

  function setSubtitle() {
    if (!subtitles.list) return

    let index: number = 0

    const videoDuration = Math.round(videoRef?.current?.duration || 0),
      videoTime = Math.round(videoRef?.current?.currentTime || 0),
      subtitleMax = subtitles.list.length,
      indexMultiplier = subtitleMax / videoDuration

    index = Math.round(videoTime * indexMultiplier)

    if (index !== subtitles.index) {
      const currentLine = subtitles.list[index];
      setVideoSubtitles(c => ({ ...c, index, currentLine }));
    }

  }

  const onLikeVideoClick = useCallback(() => {  
    AnswerServiceInstance?.likeAnswer(user.id + '', selectedAnswer?.id + '').then((data: interfaces.LikeInterface) => {
      const updatedAnswer: interfaces.AnswerInterface = {
        ...selectedAnswer,
        liked: data?.enabled,
        likes: !data?.enabled ? (selectedAnswer?.likes || 1) - 1 : (selectedAnswer?.likes || 1) + 1
      } as interfaces.AnswerInterface;

      handleVideoUpdate(updatedAnswer);
    });
  }, [handleVideoUpdate, selectedAnswer, user]);

  const handleDonateClick = useCallback(() => {
    TrackerServiceInstance?.trackEvent('clickDonate', {
      event_category: 'Donate'
    });

    window.open(client?.donationURL, '_blank', 'noreferrer');
  }, [client]);

  const onFeedbackClick = useCallback(
    (mark: string) => {
      TrackerServiceInstance?.trackEvent('videoFeedbackSelected', {
        event_category: 'Answers',
        videoFeedbackAmount: mark,
        answerId: selectedAnswer?.id
      }).then(() => {
        setVideoState(cur => ({ ...cur, isFeedbackSubmitted: true }));
      });
    },
    [selectedAnswer]
  );

  const onReplayClick = useCallback(() => {
    if (!videoRef.current) return

    setVideoState(cur => ({ ...cur, isFeedbackShown: false, ended: false, started: true, paused: false }));

    videoRef.current.currentTime = 0;

    setTimeout(() => {
      if (videoRef.current) videoRef.current.play();
    }, 10);
  }, []);

  const DonationBanner = () => {
    const defaultDonationText: string = `${t("Support")} ${client?.name} ${t("now by making a donation to the campaign")}!`;

    const customStyles = {
      textColor: client?.plusAskPillColour ? { color: client.plusAskPillColour } : {},
      backgroundColor: client?.plusAskPillColour ? {
        backgroundColor: client.plusAskPillColour,
        borderColor: client.plusAskPillColour
      } : {},
    }


    return <div style={customStyles.textColor} className={classes.donationBanner}>
      <div className={classes.donationInnerWrapper}>
        <Icons iconType="bullhorn" iconClass={classes.donationIcon} />
        <span className={classes.donationText}>
          {translateCustomText("donateText", defaultDonationText)}
        </span>
      </div>

      <button type="button" className={cn(ButtonClasses.Button, classes.donateNowButton)}
        style={customStyles.backgroundColor}
        onClick={() => handleDonateClick()}
      >
        {translateCustomText("donateCtaText", t("Donate Now"))}
      </button>
    </div>
  }

  function getVideoFeedbackTemplate() {
    return <div className={cn(classes.feedbackBlock, videoState.isFeedbackShown && videoState.ended && classes.activated)}>
      <div className={classes.replayVideo} onClick={() => onReplayClick()}>
        <Icons iconType="redo" iconClass={classes.replayIcon} />
      </div>

      {videoState.isFeedbackSubmitted ? (
        <div className={classes.thankYouText}>Thanks for your Feedback!</div>
      ) : (
        <>
          <div className={classes.leaveFeedbackText}>
            {translateCustomText("postVideoSurveyQuestion",
              t(
                client.textOptions?.postVideoSurveyQuestion ||
                `Does this answer make you more likely to support ${client.name}?`
              )
            )}
          </div>

          <div className={classes.feedbackMarkBlock}>
            <div className={classes.feedbackValues}>
              <span onClick={() => onFeedbackClick('1')}>1</span>
              <span onClick={() => onFeedbackClick('2')}>2</span>
              <span onClick={() => onFeedbackClick('3')}>3</span>
              <span onClick={() => onFeedbackClick('4')}>4</span>
              <span onClick={() => onFeedbackClick('5')}>5</span>
            </div>

            <div className={classes.feedbackExplanation}>
              <span>
                {translateCustomText("postVideoSurveyLessLikely", client.textOptions?.postVideoSurveyLessLikely || t("Less Likely"))}
            </span>
              <span>
                {translateCustomText("postVideoSurveyNeutral", client.textOptions?.postVideoSurveyNeutral || t("Neutral"))}
              </span>
              <span>
                {translateCustomText("postVideoSurveyMoreLikely", client.textOptions?.postVideoSurveyMoreLikely || t("More Likely"))}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  }

  function getMetaInfoTemplate() {
    return <>
      {showMetaData && <div className={classes.title}>
        <div className={classes.titleText}>{getQuestionTranslation()}</div>
        <div className={classes.meta}>
          <div className={classes.user}>
            <IconAvatar categoryIcon={selectedAnswer?.question?.category} wrapperClass={classes.iconWrapper} />

            <div className={classes.text}>
              {selectedAnswer?.question?.user?.firstName} • {' '}
              {selectedAnswer?.question?.user?.zip || '00000'} • {selectedAnswer?.question?.votes} {' '}
              {(selectedAnswer?.question?.votes || 0) > 1 ? t("votes") : t("vote")} • {' '}
              {new Date(selectedAnswer?.createdAt || 0).toLocaleString('default', { month: 'short', day: 'numeric' })}
            </div>
          </div>
        </div>
      </div>}

      <div className="likesCounterWrapper">
        <CounterButton
          buttonClass={classes.likesCounter}
          buttonText={selectedAnswer?.liked ? 'Liked' : 'Like'}
          buttonCount={selectedAnswer?.likes || 1}
          askButtonColor={client?.plusAskPillColour}
          isButtonActive={selectedAnswer?.liked}
          handleClick={() => onLikeVideoClick()}
          hasLikedVideo={true}
        />
      </div>
    </>
  }

  useEffect(() => {
    if (videoIsPlaying)
      setVideoState(c => ({ ...c, isFeedbackShown: false,  ended: false }));
  }, [selectedAnswer, videoIsPlaying])

  useEffect(() => {
    if (!selectedAnswer) return

    setVideoState(c => ({
      ...c,
      ended: false,
      started: false,
      paused: false,
      seeked: true,
      seeking: false,
      isDonationBannerShown: false,
      isFeedbackShown: false,
      isFeedbackSubmitted: false,
    }));
  }, [selectedAnswer])

  useEffect(() => {
    if (videoIsPaused || !selectedAnswer?.subtitles)
      setVideoSubtitles({} as VideoSubtitleStateInterface)
    else
      setSubtitles()
  }, [selectedAnswer?.id, selectedAnswer, selectedAnswer?.subtitles, videoIsPaused, videoIsPlaying, language])

  return (
    <div className={cn(classes.SelectedAnswer, 'SelectedAnswer', videoState.isFullScreen && classes.fullScreen)}>
      <div className={classes.controlVideoWrapper}>
        <canvas ref={canvasRef} className={classes.videoBackground}></canvas>

        <video
          id={isMobile ? "mobile_video_player" : "video_player"}
          ref={videoRef}
          className={classes.videoPlayer}
          poster={selectedAnswer && selectedAnswer.imageUrl}
          src={selectedAnswer?.videoUrls?.mp4 || selectedAnswer?.videoUrl}
          playsInline
          muted={muted}
          onLoadedData={() => {
            if (canvasRef.current) {
              canvasRef.current.width  = videoRef.current.videoWidth;
              canvasRef.current.height = videoRef.current.videoHeight;
              if (selectedAnswer) {
                const previewPoster = new Image();
                previewPoster.src = selectedAnswer.imageUrl;
                videoBackgroundContext?.drawImage(previewPoster, 0, 0);
              }
            }
          }}
          onPlay={drawBackground}
          onSeeked={() => {videoBackgroundContext?.drawImage(videoRef.current, 0, 0)}}
          onTimeUpdate={setSubtitle}
        >
          {selectedAnswer?.videoUrls?.mp4  && <source src={selectedAnswer.videoUrls.mp4 || selectedAnswer?.videoUrl} type="video/mp4" />}
          {selectedAnswer?.videoUrls?.webm && <source src={selectedAnswer.videoUrls.webm} type="video/webm" />}
          {selectedAnswer?.videoUrls?.ogv  && <source src={selectedAnswer.videoUrls.ogv}  type="video/ogv"  />}
        </video>

        <FontAwesomeIcon
          icon={faSpinner}
          className={cn(classes.seekingSpinner, videoState.seeking && classes.seeking)}
          pulse
        />

        {getMetaInfoTemplate()}

        {subtitles.enabled && subtitles.currentLine !== '' && <div className={classes.subtitlesContainer}>
          <div className={classes.subtitleLine}>{subtitles.currentLine}</div>
        </div>}

        <VideoControl
          selectedAnswer={selectedAnswer && selectedAnswer}
          videoRef={videoRef}
          videoState={videoState}
          muted={muted}
          setMuted={setMuted}
          updateVideoState={v => setVideoState(cur => ({ ...cur, ...v }))}
          onFullScreen={v => setVideoState(cur => ({ ...cur, isFullScreen: v }))}
        />

        {getVideoFeedbackTemplate()}
      </div>

      <div className={cn(classes.mobileMetaInfo, 'mobileMetaInfo')}>
        {getMetaInfoTemplate()}
      </div>

      {getVideoFeedbackTemplate()}

      {client?.donationURL && <DonationBanner />}
    </div>
  );
}
